import { TaskSubTicketResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';

export interface DesignerFilterOption {
    id: ID;
    firstName: string;
    lastName: string;
    fullName: string;
    imageHash: ImageHash | null;
}

// Case- and locale-insensitive enough to be stable across browsers: the list is short and the
// only requirement is that the same rows always produce the same order
const byName = (a: DesignerFilterOption, b: DesignerFilterOption): number => {
    const byFirst = a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase());
    if (byFirst !== 0) {
        return byFirst;
    }

    const byLast = a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
    if (byLast !== 0) {
        return byLast;
    }

    // Two people with the same name are still two people, so the id breaks the tie rather than
    // letting their order depend on which row happened to load first
    return a.id.localeCompare(b.id);
};

// The designers actually holding work on THIS Request, derived from the rows the list loaded.
//
// Derived from the loaded rows rather than from the already-filtered ones: a filter whose
// options disappear as soon as it is used cannot be undone from its own popover. It is also
// deliberately not the designer picker — that names everyone *assignable* here, which is a
// different and much longer list than everyone *assigned* here.
//
// Archived rows contribute their assignee too. Their rows are only hidden by the archived
// toggle, so dropping their designer would make "Show archived" silently narrow this list.
export const resolveDesignerFilterOptions = (
    subTickets: TaskSubTicketResource[] | null
): DesignerFilterOption[] => {
    if (!subTickets) {
        return [];
    }

    const byId = new Map<ID, DesignerFilterOption>();

    for (const subTicket of subTickets) {
        const user = subTicket.assignee_user;
        if (!user || byId.has(user.id)) {
            continue;
        }

        byId.set(user.id, {
            id: user.id,
            firstName: user.first_name,
            lastName: user.last_name,
            fullName: `${user.first_name} ${user.last_name}`.trim(),
            imageHash: user.image_hash,
        });
    }

    return Array.from(byId.values()).sort(byName);
};
